/**
 * How many clips the whole display may be pulling at once.
 *
 * Every media byte a wall fetches now goes through here — the clip a tile is switching to
 * as well as the one it is reading ahead — so this number IS the wall's concurrency, not a
 * budget layered on top of whatever the browser was already doing.
 *
 * Four, because over HTTP/1.1 a browser opens at most six connections per origin and the
 * remaining two have to carry `/api/source/fetch`, `/api/ping` and the display socket.
 * Overshooting does not make the link faster; it just moves the queue from here (where a
 * waiting tile keeps playing its current clip) into the browser (where everything else
 * queues behind it too). Serving HTTP/2 raises the real ceiling — see
 * docs/perf/transport.md — but not this one, which is about the link rather than the
 * protocol.
 */
const MAX_CONCURRENT = 4;

/**
 * Give up waiting on a warm after this long and let the media element fetch it itself.
 *
 * Deliberately generous. A slow link is NOT the case this exists for — there, waiting is
 * the correct behaviour and the tile goes on playing what it already has. This is for a
 * fetch that will never finish at all, where the alternative is a tile frozen forever.
 */
const WARM_TIMEOUT_MS = 20_000;

/**
 * How long a warm may sit in the QUEUE before it gives up its place and lets the element
 * fetch for itself.
 *
 * WARM_TIMEOUT_MS above starts only once a slot is held, so it never bounded the wait
 * for one — and `release()` hands every freed slot to a `now` first. On a wall whose
 * tiles turn over faster than four fetches can land (Maximum Dopamine: sixteen tiles of
 * 0–3s clips), the `next` read-aheads could sit behind a permanent stream of `now`s and
 * a `now` could sit behind fifteen others; a tile waiting on either showed nothing new
 * until REVEAL_TIMEOUT_MS gave up on it. The wait is still the right default — a tile
 * that waits keeps playing — but it has to end.
 */
const QUEUE_WAIT_MS = 15_000;

/** How many recently-warmed urls to remember, so a clip that comes round again in a
 *  short cycle isn't re-fetched just to be told it's already cached. */
const REMEMBERED = 600;

/**
 * `now` is a tile that has nothing to show until this lands; `next` is reading ahead.
 * A tile waiting outranks a tile topping up, and without the distinction a burst of
 * read-ahead can sit in front of the clip somebody is actually waiting for.
 */
type WarmPriority = "now" | "next";

interface Waiter {
  priority: WarmPriority;
  start: () => void;
}

const inflight = new Map<string, { promise: Promise<void>; waiter?: Waiter }>();
const warmed = new Set<string>();
const warmedOrder: string[] = [];
let active = 0;
const queue: Waiter[] = [];

function schedule(priority: WarmPriority): { gate: Promise<void>; waiter?: Waiter } {
  if (active < MAX_CONCURRENT) {
    active++;
    return { gate: Promise.resolve() };
  }
  let waiter!: Waiter;
  const gate = new Promise<void>((resolve) => {
    waiter = {
      priority,
      start: () => {
        active++;
        resolve();
      },
    };
    queue.push(waiter);
  });
  return { gate, waiter };
}

function release(): void {
  active--;
  // Highest priority first, oldest within a priority.
  let i = queue.findIndex((w) => w.priority === "now");
  if (i === -1) i = 0;
  const next = queue.splice(i, 1)[0];
  next?.start();
}

function remember(url: string): void {
  if (warmed.has(url)) return;
  warmed.add(url);
  warmedOrder.push(url);
  while (warmedOrder.length > REMEMBERED) {
    const old = warmedOrder.shift();
    if (old !== undefined) warmed.delete(old);
  }
}

/**
 * Pull a clip into the browser's HTTP cache.
 *
 * ## Why a fetch and not a hidden `<video preload="auto">`
 *
 * That is what this replaced, and the swap was not cosmetic — the element version
 * downloaded every clip TWICE. Measured on a 3x4 wall at 1080p on a five-second timer:
 * 282 media requests for 144 distinct clips, and 56% of the repeats fell 4.5–5.5s apart,
 * which is exactly one timer beat — the preloader fetching clip N+1, then the slot element
 * fetching the same file again when the tile got to it. A media element does not hand its
 * bytes to the next element to ask for that url, `immutable` response or not
 * (`SyncedRegion` found the same thing for two elements playing at once).
 *
 * A `fetch()` does. Verified in the browser: warm a url with fetch, then point a fresh
 * `<video>` at it, and the element's request reports `transferSize: 0`.
 *
 * ## Why the caller waits for it
 *
 * Racing this against the element — mount the element AND warm in parallel, hope the warm
 * wins — works on a fast link and fails on a slow one, which is precisely backwards.
 * Measured under a 12 Mbit/s / 60 ms link: repeats went from ~1 per run to **102 of 265
 * requests**, because the warm had not reached the cache by the time the element asked, so
 * both fetched. The double download came back exactly when the link could least afford it.
 *
 * So `RegionPlayer` waits for `now` before mounting anything. The tile keeps playing the
 * clip it already has while it waits, which is what the readiness gate was already for, and
 * the wall degrades by switching less often instead of by stampeding a full link.
 *
 * Best-effort throughout: a failure, an offline blip, an evicted cache entry or a timeout
 * all resolve normally and the element fetches it the usual way. Never rejects.
 */
export function warmMedia(url: string, priority: WarmPriority = "next"): Promise<void> {
  if (!url || warmed.has(url)) return Promise.resolve();

  const running = inflight.get(url);
  if (running) {
    // Someone now needs what was only being read ahead. Move it up the queue rather
    // than starting a second fetch for the same bytes.
    if (priority === "now" && running.waiter) running.waiter.priority = "now";
    return running.promise;
  }

  const { gate, waiter } = schedule(priority);
  const controller = new AbortController();

  const promise = (async () => {
    if (waiter) {
      // Queued. Wait for a slot, but not forever: a waiter that times out leaves the
      // queue without ever having taken a slot, so there is nothing to release, and
      // resolves normally — the caller mounts its element and the browser fetches.
      let queueTimer: ReturnType<typeof setTimeout> | undefined;
      const gaveUp = await Promise.race([
        gate.then(() => false),
        new Promise<boolean>((resolve) => {
          queueTimer = setTimeout(() => resolve(true), QUEUE_WAIT_MS);
        }),
      ]);
      clearTimeout(queueTimer);
      if (gaveUp) {
        const i = queue.indexOf(waiter);
        if (i !== -1) queue.splice(i, 1);
        return;
      }
    } else {
      await gate;
    }
    const timer = setTimeout(() => controller.abort(), WARM_TIMEOUT_MS);
    try {
      const res = await fetch(url, { credentials: "same-origin", signal: controller.signal });
      if (!res.ok || !res.body) return;
      // Drain and discard: the point is the HTTP cache entry the response leaves behind,
      // not the bytes — holding them would put every upcoming clip on the wall in memory.
      const reader = res.body.getReader();
      for (;;) {
        const { done } = await reader.read();
        if (done) break;
      }
      remember(url);
    } catch {
      /* aborted, offline, evicted — the element will fetch it itself */
    } finally {
      clearTimeout(timer);
      release();
    }
  })();

  const entry = { promise, waiter };
  inflight.set(url, entry);
  void promise.finally(() => {
    if (inflight.get(url) === entry) inflight.delete(url);
  });
  return promise;
}
